import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Divider,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Today as TodayIcon,
  EventNote as FristIcon,
  OpenInNew as OpenIcon,
  Refresh as RefreshIcon,
  Done as DoneIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { fristenApi } from '../services/api';
import type { Frist, FristStatistik } from '../types/api';

const DashboardFristenWidget: React.FC = () => {
  const navigate = useNavigate();
  const [fristen, setFristen] = useState<Frist[]>([]);
  const [statistik, setStatistik] = useState<FristStatistik | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [fristenRes, statistikRes] = await Promise.all([
        fristenApi.getAll({ nurDieseWoche: true }),
        fristenApi.getStatistik(),
      ]);

      // Filtere und sortiere: Überfällige zuerst, dann nach Datum
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const sorted = fristenRes.data
        .filter((f) => f.status !== 'Erledigt')
        .sort((a, b) => {
          const dateA = new Date(a.faelligkeitsdatum);
          const dateB = new Date(b.faelligkeitsdatum);
          const isOverdueA = dateA < today;
          const isOverdueB = dateB < today;

          if (isOverdueA && !isOverdueB) return -1;
          if (!isOverdueA && isOverdueB) return 1;
          return dateA.getTime() - dateB.getTime();
        })
        .slice(0, 8);

      setFristen(sorted);
      setStatistik(statistikRes.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Fristen:', err);
      setError('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsErledigt = async (fristId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await fristenApi.markAsErledigt(fristId);
      loadData();
    } catch (err) {
      console.error('Fehler beim Erledigen:', err);
    }
  };

  const getFristStatus = (frist: Frist) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fristDate = new Date(frist.faelligkeitsdatum);
    fristDate.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((fristDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { color: 'error' as const, label: `${Math.abs(diffDays)}d überfällig`, icon: <WarningIcon fontSize="small" /> };
    }
    if (diffDays === 0) {
      return { color: 'warning' as const, label: 'Heute', icon: <TodayIcon fontSize="small" /> };
    }
    if (diffDays <= 3) {
      return { color: 'warning' as const, label: `In ${diffDays}d`, icon: <ScheduleIcon fontSize="small" /> };
    }
    return { color: 'info' as const, label: `In ${diffDays}d`, icon: <ScheduleIcon fontSize="small" /> };
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return null; // Silent fail - don't show widget if Fristen table doesn't exist yet
  }

  const hasUrgentFristen = statistik && (statistik.ueberfaellig > 0 || (statistik.dieseWoche > 0));

  return (
    <Paper
      sx={{
        p: 3,
        mb: 4,
        borderLeft: hasUrgentFristen ? '4px solid' : undefined,
        borderColor: statistik?.ueberfaellig ? 'error.main' : 'warning.main',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <FristIcon color={statistik?.ueberfaellig ? 'error' : 'warning'} />
          <Typography variant="h6">Anstehende Fristen</Typography>
          {statistik?.ueberfaellig ? (
            <Chip
              icon={<WarningIcon />}
              label={`${statistik.ueberfaellig} überfällig`}
              color="error"
              size="small"
            />
          ) : null}
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Aktualisieren">
            <IconButton size="small" onClick={loadData}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            endIcon={<OpenIcon />}
            onClick={() => navigate('/fristen')}
          >
            Alle anzeigen
          </Button>
        </Box>
      </Box>

      {/* Statistik-Übersicht */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <Chip
          icon={<WarningIcon />}
          label={`${statistik?.ueberfaellig || 0} überfällig`}
          color="error"
          variant={statistik?.ueberfaellig ? 'filled' : 'outlined'}
          size="small"
          onClick={() => navigate('/fristen?filter=ueberfaellig')}
          sx={{ cursor: 'pointer' }}
        />
        <Chip
          icon={<TodayIcon />}
          label={`${statistik?.dieseWoche || 0} diese Woche`}
          color="warning"
          variant="outlined"
          size="small"
          onClick={() => navigate('/fristen?filter=dieseWoche')}
          sx={{ cursor: 'pointer' }}
        />
        <Chip
          icon={<FristIcon />}
          label={`${statistik?.offen || 0} offen`}
          variant="outlined"
          size="small"
          onClick={() => navigate('/fristen')}
          sx={{ cursor: 'pointer' }}
        />
      </Box>

      {fristen.length === 0 ? (
        <Box textAlign="center" py={2}>
          <Typography color="textSecondary">
            Keine dringenden Fristen diese Woche
          </Typography>
        </Box>
      ) : (
        <List dense disablePadding>
          {fristen.map((frist, index) => {
            const status = getFristStatus(frist);
            return (
              <React.Fragment key={frist.fristID}>
                {index > 0 && <Divider />}
                <ListItem
                  sx={{
                    py: 1,
                    px: 0,
                    bgcolor: status.color === 'error' ? 'error.lighter' : undefined,
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => navigate(`/fristen?id=${frist.fristID}`)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Box sx={{ color: `${status.color}.main` }}>{status.icon}</Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                          {frist.titel}
                        </Typography>
                        <Chip
                          label={frist.kategorie}
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <BusinessIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                        <Typography variant="caption" color="textSecondary" noWrap sx={{ maxWidth: 150 }}>
                          {frist.adresseName}
                        </Typography>
                        <Chip
                          label={status.label}
                          size="small"
                          color={status.color}
                          sx={{ height: 16, fontSize: '0.6rem' }}
                        />
                      </Box>
                    }
                  />
                  <Tooltip title="Als erledigt markieren">
                    <IconButton
                      size="small"
                      color="success"
                      onClick={(e) => handleMarkAsErledigt(frist.fristID, e)}
                    >
                      <DoneIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      )}
    </Paper>
  );
};

export default DashboardFristenWidget;
